import React, { useState } from 'react';
import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { AvatarColors } from 'types/profile';
import { ChakraAvatar, UploadImageModal } from 'app/components/Core';
import { useAppDispatch } from 'app/store';
import {
  deleteProfileImageEffect,
  fetchProfileImageStatusEffect,
  uploadProfileImageEffect,
} from 'app/store/Profile/effects';
import { ChakraConfirmationDialog } from 'app/components/Dialogs';
import { useImageUploadPolling } from './hooks';

interface ProfileImageProps {
  image?: string;
  initials?: string;
  colors?: AvatarColors;
}

export const ProfileImage: React.FC<ProfileImageProps> = ({
  image,
  initials,
  colors,
}) => {
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const startPolling = useImageUploadPolling(() =>
    dispatch(fetchProfileImageStatusEffect()).unwrap()
  );

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteProfileImageEffect());
      setDeleteOpen(false);
    } catch (e) {
      // proceed
    } finally {
      setIsDeleting(false);
    }
  };

  const styles = useMultiStyleConfig('ProfileImage', {});
  return (
    <>
      <Menu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        placement="bottom"
        offset={[60, -16]}
      >
        <Flex
          sx={styles.container}
          onClick={() => (image ? setMenuOpen(true) : setImageUploadOpen(true))}
          data-testid="open-image-upload"
        >
          {image ? (
            <img
              data-testid="profile-avatar"
              alt="profile avatar"
              src={image}
              width="100%"
              height="100%"
            />
          ) : (
            <ChakraAvatar colors={colors} initials={initials} size="3xl" />
          )}
          <Flex className="editOverlay" sx={styles.editOverlay}>
            <MenuButton sx={{ alignItems: 'baseline' }}>
              <Text fontSize="xxs" fontWeight={600} color="white">
                EDIT
              </Text>
            </MenuButton>
          </Flex>
        </Flex>
        <MenuList>
          <MenuItem onClick={() => setImageUploadOpen(true)}>
            Change image
          </MenuItem>
          <MenuItem onClick={() => setDeleteOpen(true)} color="R.80">
            Remove image
          </MenuItem>
        </MenuList>
      </Menu>
      <UploadImageModal
        title="Profile Image"
        open={imageUploadOpen}
        onClose={() => setImageUploadOpen(false)}
        startPolling={startPolling}
        effect={uploadProfileImageEffect}
      />
      <ChakraConfirmationDialog
        isOpen={deleteOpen}
        title="Remove Image"
        message="Are you sure you want to remove the current profile image?"
        actionButtonText="Delete"
        handleAction={handleDelete}
        handleClose={() => setDeleteOpen(false)}
        disableActions={isDeleting}
      />
    </>
  );
};
